import { type VariantProp, type VariantProps, cv } from "@suse-o11y/class-variance";
import React from "react";
import { NavLink, type NavLinkProps } from "react-router-dom";

import type { PolymorphicBoxProps } from "~/components/polymorphic-box";

import styles from "./link.module.css";

const variants = cv(styles.link, {
	variants: {
		theme: {
			// inherits the color of the parent element without underline (default)
			unstyled: styles.unstyled,
			// blue without underline
			primary: styles.primary,
			// dark with underline
			secondary: styles.secondary,
			// TODO: STAC-20513
			// 400 with underline
			tertiary: styles.tertiary,
			// light with underline, for dark backgrounds
			light: styles.light,
		},
	},

	defaultVariants: {
		theme: "unstyled",
	},
});

export type Theme = VariantProp<typeof variants, "theme">;

interface LinkOwnProps extends Omit<NavLinkProps, "to">, VariantProps<typeof variants> {
	to?: string;

	/**
	 * When true, the link is disabled and cannot be clicked.
	 * Uses `span` element instead of `NavLink` component.
	 *
	 * @default false
	 */
	disabled?: boolean;
}

export type LinkProps<TElement extends React.ElementType = typeof NavLink> = PolymorphicBoxProps<
	TElement,
	LinkOwnProps
>;

export const Link: <TElement extends React.ElementType = typeof NavLink>(
	props: LinkProps<TElement>,
) => React.ReactNode = React.forwardRef(
	(
		{ to, theme, className, disabled, style, ...props }: PolymorphicBoxProps<React.ElementType, LinkOwnProps>,
		ref: React.ForwardedRef<HTMLAnchorElement>,
	) => {
		if (disabled) {
			const Element = props.el ?? "span";

			return (
				<Element
					aria-disabled={disabled}
					className={variants({ theme, className })}
					data-to={to}
					ref={ref}
					role="button"
					style={style}
					{...props}
				/>
			);
		}

		const Element = props.el ?? (props.href == null ? NavLink : "a");

		return (
			<Element
				className={variants({ theme, className })}
				ref={ref}
				rel={props.target === "_blank" ? "noopener noreferrer" : props.rel}
				style={style}
				to={to ?? ""}
				{...props}
			/>
		);
	},
);
